import { useCallback, useEffect, useRef, useState } from 'react';
import { faCaretDown } from '@fortawesome/pro-solid-svg-icons/faCaretDown';
import { faCaretUp } from '@fortawesome/pro-solid-svg-icons/faCaretUp';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Anchor, Group, Stack, Text, Textarea } from '@mantine/core';
import { FormattedMessage, useIntl } from 'react-intl';

import { pasteAddressSectionMessages } from './paste-address-section-messages';
import { parseAddress } from '../../../../address/address-validation.api';

import type { LtlAddress } from '../../../../types/ltl';

const DEBOUNCE_MS = 300;

type PasteAddressSectionProps = {
  onParsedAddress: (parsed: Partial<LtlAddress>) => void;
  onPasteAddressUsed?: () => void;
};

export const PasteAddressSection = ({ onParsedAddress, onPasteAddressUsed }: PasteAddressSectionProps) => {
  const intl = useIntl();
  const [isExpanded, setIsExpanded] = useState(false);
  const [rawText, setRawText] = useState('');
  const [hasParseError, setHasParseError] = useState(false);

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const prevTextRef = useRef('');
  const abortControllerRef = useRef<AbortController | null>(null);
  const hasFiredUsageRef = useRef(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (isExpanded) textareaRef.current?.focus();
  }, [isExpanded]);

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  const handleChange = useCallback(
    (value: string) => {
      setRawText(value);

      if (debounceRef.current) clearTimeout(debounceRef.current);

      debounceRef.current = setTimeout(async () => {
        if (value === prevTextRef.current) return;
        prevTextRef.current = value;

        if (!value) {
          setHasParseError(false);
          return;
        }

        abortControllerRef.current?.abort();
        const controller = new AbortController();
        abortControllerRef.current = controller;

        try {
          const parsed = await parseAddress(value, controller.signal);
          setHasParseError(false);
          const line2 = parsed.line3 ? `${parsed.line2} ${parsed.line3}`.trim() : parsed.line2;
          const fields: Partial<LtlAddress> = {
            name: parsed.name ?? '',
            company: parsed.company ?? '',
            line1: parsed.line1 ?? '',
            line2: line2 ?? '',
            city: parsed.city ?? '',
            state: parsed.state ?? '',
            postalCode: parsed.postalCode ?? '',
            countryCode: parsed.countryCode ?? '',
          };
          onParsedAddress(fields);
          if (!hasFiredUsageRef.current) {
            hasFiredUsageRef.current = true;
            onPasteAddressUsed?.();
          }
        } catch {
          if (!controller.signal.aborted) setHasParseError(true);
        }
      }, DEBOUNCE_MS);
    },
    [onParsedAddress, onPasteAddressUsed],
  );

  return (
    <Stack gap='xs'>
      <Group justify='flex-end'>
        <Anchor component='button' onClick={() => setIsExpanded((prev) => !prev)} size='sm' type='button'>
          <Group gap={6}>
            <FormattedMessage {...pasteAddressSectionMessages.toggleLabel} />
            <FontAwesomeIcon icon={isExpanded ? faCaretUp : faCaretDown} />
          </Group>
        </Anchor>
      </Group>

      {isExpanded && (
        <Stack gap='xs'>
          <Text fw={600} size='sm'>
            <FormattedMessage {...pasteAddressSectionMessages.header} />
          </Text>
          <Text c='dimmed' size='sm'>
            <FormattedMessage {...pasteAddressSectionMessages.description} />
          </Text>
          <Textarea
            ref={textareaRef}
            error={hasParseError ? intl.formatMessage(pasteAddressSectionMessages.parseError) : undefined}
            label={intl.formatMessage(pasteAddressSectionMessages.label)}
            minRows={4}
            onChange={(e) => handleChange(e.currentTarget.value)}
            value={rawText}
          />
        </Stack>
      )}
    </Stack>
  );
};
